import React, { useCallback, useEffect, useState, useRef } from "react";
import { DevProject } from "@interfaces/app.interface";
import { BaseLayout } from "@layouts/BaseLayout";
import { getAllDevelopersProjects } from "@services/krentDeveloper";
import { Projectcard } from "@shared/cards/ProjectCard";
import DevelopersPagination from "@shared/navbars/DevelopersNavbar/Pagination";
import {
  getPageFromSessionStorage,
  addPageToSessionStorage,
} from "@utils/helpers";

const ITEMS_PER_PAGE = 9;

const DeveloperAccount = () => {
  const [projects, setProjects] = useState<DevProject[]>([]);
  const [currentPage, setCurrentPage] = useState(() => {
    const storedPage = parseInt(getPageFromSessionStorage());
    return isNaN(storedPage) ? 1 : storedPage;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [keyWord, setKeyWord] = useState("");
  const [keyWordLoading, setKeyWordLoading] = useState(false);
  const prevKeyWord = useRef("");

  const fetchProjects = useCallback(
    async (page: number, searchTerm?: string) => {
      setLoading(true);
      setError(null);
      const abortController = new AbortController();

      try {
        const response = await getAllDevelopersProjects(
          ITEMS_PER_PAGE,
          page,
          abortController.signal,
          searchTerm || ""
        );
        setProjects((response.data as any) || []);
        setTotalPages(Math.ceil(response.pagination?.totalPages || 1));
      } catch (error) {
        setError("Failed to fetch projects. Please try again.");
        console.error(error);
      } finally {
        setLoading(false);
      }

      return () => {
        abortController.abort();
      };
    },
    []
  );

  useEffect(() => {
    fetchProjects(currentPage);
  }, [fetchProjects, currentPage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeyWord(e.target.value);
  };

  const handleInputClear = () => {
    setKeyWord("");
    fetchProjects(1);
  };

  const onSearchKeyWord = async () => {
    setKeyWordLoading(true);
    try {
      await fetchProjects(1, keyWord);
      addPageToSessionStorage(1);
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
    } finally {
      setKeyWordLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    addPageToSessionStorage(newPage);
    fetchProjects(newPage, keyWord);
  };

  return (
    <BaseLayout>
      <div className="w-80 mx-auto mt-4">
        <div className="mt-4">
          <h6 className="my-4">Developer projects</h6>
          <div className="bg-white shadow-sm rounded-4 p-3 w-100">
            <form
              className="d-flex flex-column flex-md-row align-items-stretch gap-2 w-100"
              onSubmit={(e) => {
                e.preventDefault();
                onSearchKeyWord();
              }}
            >
              <div className="input-group flex-grow-1 rounded-pill border bg-light overflow-hidden align-items-center">
                <span className="input-group-text bg-transparent border-0 ps-3 d-flex align-items-center">
                  <i className="fa fa-search text-muted fs-5"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-0 bg-transparent ps-2 py-2"
                  placeholder="Search developer projects..."
                  value={keyWord}
                  onChange={handleInputChange}
                  onInput={(e: any) => {
                    const currentKeyWord = e.target.value;
                    if (prevKeyWord.current && currentKeyWord === "") {
                      handleInputClear();
                    }
                    prevKeyWord.current = currentKeyWord;
                  }}
                />
                {keyWord && (
                  <button
                    type="button"
                    className="btn bg-transparent border-0 pe-3 d-flex align-items-center"
                    onClick={handleInputClear}
                  >
                    <i className="fa fa-times text-danger fs-6"></i>
                  </button>
                )}
              </div>

              <button
                type="submit"
                className="btn btn-primary rounded-pill px-4 fw-semibold"
                disabled={keyWordLoading}
              >
                {keyWordLoading ? "Searching..." : "Search"}
              </button>
            </form>

            {error && (
              <p className="text-danger mt-2 small text-center">{error}</p>
            )}
          </div>
        </div>

        <div className="my-10">
          <div className="row row-cols-1 row-cols-md-3 g-5 mb-5 w-full">
            {loading &&
              [...new Array(6).fill(0)].map((_, j) => (
                <div className="col" key={j}>
                  <div
                    className="card border-light shadow-sm"
                    style={{ height: "25rem" }}
                    aria-hidden="true"
                  >
                    <svg
                      className="bd-placeholder-img card-img-top"
                      width="100%"
                      height="180"
                      xmlns="http://www.w3.org/2000/svg"
                      role="img"
                      aria-label="Placeholder"
                      preserveAspectRatio="xMidYMid slice"
                      focusable="false"
                    >
                      <rect width="100%" height="100%" fill="#868e96"></rect>
                    </svg>
                    <div className="card-body">
                      <h5 className="card-title placeholder-glow">
                        <span className="placeholder col-6"></span>
                      </h5>
                      <p className="card-text placeholder-glow">
                        <span className="placeholder col-7"></span>
                        <span className="placeholder col-4"></span>
                        <span className="placeholder col-4"></span>
                        <span className="placeholder col-6"></span>
                        <span className="placeholder col-8"></span>
                      </p>
                      <span
                        tabIndex={-1}
                        className="btn my-4 btn-primary bg-primary disabled placeholder col-6"
                      ></span>
                    </div>
                  </div>
                </div>
              ))}
          </div>

          {!loading && !error && (
            <>
              <div className="projects-div">
                {projects.map((project, idx) => (
                  <div key={project._id || idx}>
                    <Projectcard project={project} client />
                  </div>
                ))}
              </div>
              <DevelopersPagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
      </div>
    </BaseLayout>
  );
};

export default DeveloperAccount;
